import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle, Mail } from 'lucide-react';

const API = API_BASE_URL + '/api';

export function ForgotPasswordDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleReset = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim().toLowerCase()
      });

      if (response.data.status === 'success') {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Villa kom upp. Vinsamlegast reyndu aftur.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    handleReset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleClose();
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Endurstilla lykilorð
          </DialogTitle>
          <DialogDescription>
            {success 
              ? 'Ef netfangið er skráð í kerfinu hefur verið sent endurstillingarhlekkur.'
              : 'Sláðu inn netfangið þitt og við munum senda þér hlekk til að endurstilla lykilorðið.'
            }
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-green-800 font-medium">
                  Tölvupóstur sendur!
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Athugaðu pósthólfið þitt og smelltu á hlekkinn til að endurstilla lykilorðið þitt.
                </p>
                <p className="text-xs text-green-600 mt-2">
                  Gleyma ekki að athuga ruslpóst ef þú sérð ekkert.
                </p>
              </div>
            </div>

            <Button 
              onClick={handleClose}
              className="w-full"
            >
              Loka
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Netfang</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="þitt@netfang.is"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Hætta við
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={loading || !email}
              >
                {loading ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  'Senda hlekk'
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
